import { ApplicationConfig, inject, isDevMode, LOCALE_ID, provideEnvironmentInitializer } from '@angular/core';
import {
  provideRouter,
  RouteReuseStrategy,
  withComponentInputBinding,
  withHashLocation,
  withRouterConfig,
} from '@angular/router';

import { CurrencyPipe } from '@angular/common';
import { HttpBackend, provideHttpClient, withInterceptors } from '@angular/common/http';
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { getMessaging, Messaging, provideMessaging } from '@angular/fire/messaging';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideServiceWorker } from '@angular/service-worker';
import { routes } from '@apps/mtjr/vendors/app/app.routes';
import { CreateOrderService } from '@apps/mtjr/vendors/app/orders/create-order.service';
import { environment } from '@apps/mtjr/vendors/environments/environment';
import { IonicRouteStrategy, provideIonicAngular } from '@ionic/angular/standalone';
import { BASE_CONFIG, BaseConfig } from '@libs/core/base-config';
import { AuthStorageService } from '@libs/core/lib/auth-storage-service';
import { buildAngularAuthConfig, UserScope } from '@libs/core/lib/auth/logto';
import { NgEventBus } from '@libs/core/lib/event-bus/ng-event-bus';
import { Dtos } from '@libs/mtjr/shared/generated/types';
import { clientInfoInterceptor } from '@libs/mtjr/shared/http/client-info-interceptor';

import { provideTranslateService, TranslateLoader } from '@ngx-translate/core';
import {
  AbstractSecurityStorage,
  authInterceptor,
  provideAuth,
  withAppInitializerAuthCheck,
} from 'angular-auth-oidc-client';
import { addIcons } from 'ionicons';
import {
  add,
  addCircle,
  addCircleOutline,
  card,
  caretDownSharp,
  cash,
  cashOutline,
  chatbubblesOutline,
  checkmark,
  checkmarkCircle,
  checkmarkCircleOutline,
  close,
  closeCircle,
  closeCircleOutline,
  close as closeIcon,
  copyOutline,
  createOutline,
  ellipsisVertical,
  ellipsisVerticalOutline,
  ellipsisVerticalSharp,
  gridOutline,
  image,
  imageOutline,
  informationCircleOutline,
  logoFacebook,
  logoLinkedin,
  logoTwitter,
  logoWhatsapp,
  notificationsOutline,
  paperPlane,
  pencilOutline,
  refresh,
  removeCircleOutline,
  shareOutline,
  shareSocialOutline,
  trash,
  trashOutline,
} from 'ionicons/icons';
import { MultiTranslateHttpLoader } from 'ngx-translate-multi-http-loader';

type StaticAttributeValue = Omit<Dtos.AttributeValue, 'fakeForTesting'>;
type StaticAttribute = Omit<Dtos.Attribute, 'fakeForTesting' | 'values'> & { values: StaticAttributeValue[] };

function staticAttribute(attribute: StaticAttribute): Dtos.Attribute {
  return {
    ...attribute,
    fakeForTesting: false,
    values: attribute.values.map((value) => ({ ...value, fakeForTesting: false })),
  };
}

export const attributes: Dtos.Attribute[] = [
  staticAttribute({
    label: 'Size',
    type: 'size',
    key: 'size',
    values: [
      { key: 'xxs', label: 'XXS' },
      { key: 'xs', label: 'XS' },
      { key: 's', label: 'S' },
      { key: 'm', label: 'M' },
      { key: 'l', label: 'L' },
      { key: 'xl', label: 'XL' },
      { key: '2xl', label: '2XL' },
      { key: '3xl', label: '3XL' },
      { key: '4xl', label: '4XL' },
      { key: '5xl', label: '5XL' },
      { key: '6xl', label: '6XL' },
      { key: '7xl', label: '7XL' },
      { key: '8xl', label: '8XL' },
      { key: '9xl', label: '9XL' },
      { key: '10xl', label: '10XL' },
    ],
  }),
  staticAttribute({
    label: 'Color',
    type: 'color',
    key: 'color',
    values: [
      {
        key: '#000000',
        label: 'Monochrome',
        subKeys: ['#000000', '#222222', '#444444', '#888888', '#CCCCCC', '#FFFFFF'],
      },
      {
        key: '#F44336',
        label: 'Red',
        subKeys: ['#C62828', '#D32F2F', '#E53935', '#EF5350', '#E57373', '#EF9A9A'],
      },
      {
        key: '#FF9800',
        label: 'Orange',
        subKeys: ['#F57C00', '#FB8C00', '#FF9800', '#FFA726', '#FFB74D', '#FFCC80'],
      },
      {
        key: '#FFEB3B',
        label: 'Yellow',
        subKeys: ['#FBC02D', '#FDD835', '#FFEE58', '#FFF176', '#FFF59D', '#FFF9C4'],
      },
      {
        key: '#4CAF50',
        label: 'Green',
        subKeys: ['#2E7D32', '#388E3C', '#43A047', '#66BB6A', '#81C784', '#A5D6A7'],
      },
      {
        key: '#00BCD4',
        label: 'Cyan',
        subKeys: ['#00838F', '#0097A7', '#00ACC1', '#26C6DA', '#4DD0E1', '#80DEEA'],
      },
      {
        key: '#2196F3',
        label: 'Blue',
        subKeys: ['#1565C0', '#1976D2', '#1E88E5', '#42A5F5', '#64B5F6', '#90CAF9'],
      },
      {
        key: '#673AB7',
        label: 'Purple',
        subKeys: ['#4527A0', '#512DA8', '#5E35B1', '#7E57C2', '#9575CD', '#B39DDB'],
      },
      {
        key: '#E91E63',
        label: 'Pink',
        subKeys: ['#AD1457', '#C2185B', '#D81B60', '#EC407A', '#F06292', '#F48FB1'],
      },
      {
        key: '#795548',
        label: 'Brown',
        subKeys: ['#3E2723', '#4E342E', '#5D4037', '#795548', '#8D6E63', '#A1887F'],
      },
    ],
  }),

  staticAttribute({
    label: 'Fit',
    type: 'size',
    key: 'fit',
    values: [
      { key: 'slim', label: 'Slim' },
      { key: 'regular', label: 'Regular' },
      { key: 'loose', label: 'Loose' },
    ],
  }),
  staticAttribute({
    label: 'Memory',
    type: 'size',
    key: 'memory',
    values: [
      { key: '1gb', label: '1GB' },
      { key: '2gb', label: '2GB' },
      { key: '4gb', label: '4GB' },
      { key: '8gb', label: '8GB' },
      { key: '16gb', label: '16GB' },
      { key: '32gb', label: '32GB' },
      { key: '64gb', label: '64GB' },
      { key: '128gb', label: '128GB' },
      { key: '256gb', label: '256GB' },
      { key: '512gb', label: '512GB' },
      { key: '1tb', label: '1TB' },
      { key: '2tb', label: '2TB' },
      { key: '4tb', label: '4TB' },
      { key: '8tb', label: '8TB' },
      { key: '16tb', label: '16TB' },
      { key: '32tb', label: '32TB' },
      { key: '64tb', label: '64TB' },
      { key: '128tb', label: '128TB' },
      { key: '256tb', label: '256TB' },
      { key: '512tb', label: '512TB' },
      { key: '1pb', label: '1PB' },
      { key: '2pb', label: '2PB' },
      { key: '4pb', label: '4PB' },
      { key: '8pb', label: '8PB' },
      { key: '16pb', label: '16PB' },
      { key: '32pb', label: '32PB' },
      { key: '64pb', label: '64PB' },
      { key: '128pb', label: '128PB' },
      { key: '256pb', label: '256PB' },
      { key: '512pb', label: '512PB' },
      { key: '1eb', label: '1EB' },
      { key: '2eb', label: '2EB' },
      { key: '4eb', label: '4EB' },
      { key: '8eb', label: '8EB' },
      { key: '16eb', label: '16EB' },
      { key: '32eb', label: '32EB' },
      { key: '64eb', label: '64EB' },
      { key: '128eb', label: '128EB' },
      { key: '256eb', label: '256EB' },
      { key: '512eb', label: '512EB' },
      { key: '1zb', label: '1ZB' },
      { key: '2zb', label: '2ZB' },
      { key: '4zb', label: '4ZB' },
      { key: '8zb', label: '8ZB' },
    ],
  }),
];

export const appConfig: ApplicationConfig = {
  providers: [
    // provideHttpClient(withFetch()),
    provideAuth(
      {
        config: {
          ignoreNonceAfterRefresh: true,
          secureRoutes: [`${environment.baseUrl}/api`],
          ...buildAngularAuthConfig({
            endpoint: 'https://auth.mtjr.ly/',
            appId: '8ii07hi2lm2fsst4tkf2v',
            redirectUri: environment.auth.redirectUri,
            postLogoutRedirectUri: environment.auth.postLogoutRedirectUri,
            resource: environment.auth.resource,
            scopes: [
              UserScope.Email,
              UserScope.Phone,
              UserScope.Profile,
              UserScope.CustomData,
              'openid',
              'offline_access',
            ],
          }),
          renewTimeBeforeTokenExpiresInSeconds: 180,
          silentRenew: true,
          disableCleaningPopupOnInvalidMessage: true,
        },
      },
      withAppInitializerAuthCheck(),
    ),
    { provide: AbstractSecurityStorage, useClass: AuthStorageService },
    provideRouter(
      routes,
      withRouterConfig({ paramsInheritanceStrategy: 'always' }),
      withComponentInputBinding(),
      withHashLocation(),
      // withDebugTracing(),
    ),
    provideAnimationsAsync(),
    provideIonicAngular(),
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    provideHttpClient(withInterceptors([authInterceptor(), clientInfoInterceptor])),
    { provide: LOCALE_ID, useValue: 'en' },
    {
      provide: BASE_CONFIG,
      useValue: {
        appName: 'mtjr Vendors',
        flavor: 'vendors',
        baseUrl: environment.baseUrl,
        logo: 'https://nbg1.your-objectstorage.com/mtjr/logo.svg',
        loginTitle: 'Welcome to mtjr',
        loginSubtitle: 'Sign in to start selling goods online.',
        logoHeight: '100',
        shareBaseUrl: environment.shareBaseUrl,
        authRedirectUri: environment.auth.redirectUri,
      } satisfies BaseConfig,
    },
    CurrencyPipe,
    NgEventBus,
    provideTranslateService({
      loader: {
        provide: TranslateLoader,
        useFactory: (http: HttpBackend) =>
          new MultiTranslateHttpLoader(http, [
            { prefix: '/i18n/', suffix: '/manual.json' },
            { prefix: '/i18n/', suffix: '/generated.json' },
          ]),
        deps: [HttpBackend],
      },
      lang: localStorage.getItem('lang') || 'ar',
      fallbackLang: 'ar',
    }),
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000',
    }),
    provideFirebaseApp(() =>
      initializeApp({
        projectId: 'mtjrly',
        appId: '1:963602624681:web:b1a4f5b0b9ba0cd84db9b8',
        storageBucket: 'mtjrly.firebasestorage.app',
        apiKey: 'AIzaSyBC1_5RL1db3_AWRa-kKNiikdMmPMjeN8c',
        authDomain: 'mtjrly.firebaseapp.com',
        messagingSenderId: '963602624681',
      }),
    ),
    provideMessaging(() =>
      typeof window !== 'undefined' &&
      (window.location.protocol === 'https:' || window.location.hostname === 'localhost')
        ? getMessaging()
        : ({} as Messaging),
    ),
    provideEnvironmentInitializer(() => {
      inject(CreateOrderService);
    }),
  ],
};

addIcons({
  shareIos: 'share-ios.svg',
  usdt: 'svgs/usdt.svg',
  usdc: 'svgs/usdc.svg',
  wPay: 'svgs/w-pay.svg',
  moamalat: 'svgs/moamalat.svg',
  moamalatFull: 'svgs/moamalat-full.svg',
  yussorPay: 'svgs/yussor-pay.svg',
  masrafyPay: 'svgs/masrafy-pay.svg',
  search: 'assets/icons/search.svg',
  filter: 'assets/icons/filter.svg',
  send: 'assets/icons/send.svg',
  home: 'assets/icons/home.svg',
  orders: 'assets/icons/orders.svg',
  person: 'assets/icons/user.svg',
  vendors: 'assets/icons/shop.svg',
  key: 'assets/icons/key-square.svg',
  logout: 'assets/icons/logout.svg',
  login: 'assets/icons/login.svg',
  more: 'assets/icons/more.svg',
  vendorAdd: 'assets/icons/vendor-add.svg',
  moreVer: 'assets/icons/more-ver.svg',
  star: 'assets/icons/star.svg',
  starFilled: 'assets/icons/star-filled.svg',
  starCircle: 'assets/icons/star-circle.svg',
  theme: 'assets/icons/theme.svg',
  settings: 'assets/icons/settings.svg',
  addSquare: 'assets/icons/add-square.svg',
  wallet: 'assets/icons/wallet.svg',
  moneyIn: 'assets/icons/money-in.svg',
  moneyOut: 'assets/icons/money-out.svg',
  topUp: 'assets/icons/top-up.svg',
  infoCircle: 'assets/icons/info-circle.svg',
  refund: 'assets/icons/refund.svg',
  lock: 'assets/icons/lock.svg',
  release: 'assets/icons/release.svg',
  hold: 'assets/icons/hold.svg',
  location: 'assets/icons/location.svg',
  myLocation: 'assets/icons/my-location.svg',
  caretDownSharp,
  gridOutline,
  addCircle,
  checkmarkCircleOutline,
  trash,
  imageOutline,
  closeIcon,
  addCircleOutline,
  image,
  closeCircle,
  informationCircleOutline,
  closeCircleOutline,
  checkmarkCircle,
  card,
  refresh,
  trashOutline,
  close,
  shareSocialOutline,
  shareOutline,
  crypto: '/svgs/crypto.svg',
  checkmark,
  copyOutline,
  logoFacebook,
  logoTwitter,
  logoWhatsapp,
  logoLinkedin,
  add,
  cashOutline,
  cash,
  ellipsisVertical,
  ellipsisVerticalOutline,
  ellipsisVerticalSharp,
  pencilOutline,
  removeCircleOutline,
  locationAdd: 'assets/icons/location-add.svg',
  notificationsOutline,
  createOutline,
  paperPlane,
  chatbubblesOutline,
});
